import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaCheckCircle,
  FaArrowLeft,
  FaWhatsapp,
  FaPhoneAlt,
  FaTools,
} from "react-icons/fa";

import { siteData } from "../../data/siteData";

import "./IssueDetails.css";

function IssueDetails() {
  const { slug } = useParams();

  const issues = {
    "cracked-screen": {
      title: "Cracked Screen",
      description:
        "A cracked or damaged screen can affect the display, touch response, and everyday use of your phone.",
      problems: [
        "Cracked or shattered glass",
        "Display damage",
        "Unresponsive touch",
        "Lines appearing on the screen",
        "Black spots or display issues",
      ],
    },

    "touchscreen-not-working": {
      title: "Touchscreen Not Working",
      description:
        "If your phone does not respond properly to touch, the issue may be related to the display, digitizer, or software.",
      problems: [
        "Touch not responding",
        "Partial touchscreen failure",
        "Delayed touch response",
        "Ghost touches",
        "Intermittent touch problems",
      ],
    },

    "battery-draining-fast": {
      title: "Battery Draining Fast",
      description:
        "A battery that drains unusually quickly can make your phone difficult to use throughout the day.",
      problems: [
        "Battery draining quickly",
        "Unexpected shutdowns",
        "Poor battery life",
        "Battery percentage dropping quickly",
        "Device getting warm during use",
      ],
    },

    "phone-not-charging": {
      title: "Phone Not Charging",
      description:
        "A phone that refuses to charge may have a problem with the charging system, battery, port, cable, or related hardware.",
      problems: [
        "Phone not charging",
        "Charging stops randomly",
        "Slow charging",
        "Charging connection problems",
        "Phone only charging at certain angles",
      ],
    },

    "charging-port-issues": {
      title: "Charging Port Issues",
      description:
        "A damaged or loose charging port can prevent your phone from charging correctly or connecting to other devices.",
      problems: [
        "Loose charging port",
        "Damaged charging connector",
        "Cable not staying connected",
        "Intermittent charging",
        "Charging connection failure",
      ],
    },

    "face-id-problems": {
      title: "Face ID Problems",
      description:
        "Face ID problems can prevent your device from properly recognizing your face or unlocking normally.",
      problems: [
        "Face ID not working",
        "Face recognition failure",
        "Face ID setup problems",
        "Face ID unavailable",
        "Related hardware problems",
      ],
    },

    "camera-not-working": {
      title: "Camera Not Working",
      description:
        "Camera problems can affect photography, video recording, video calls, and applications that use the camera.",
      problems: [
        "Camera not opening",
        "Black camera screen",
        "Blurry images",
        "Camera not focusing",
        "Front or rear camera failure",
      ],
    },

    "speaker-issues": {
      title: "Speaker Issues",
      description:
        "Speaker problems can cause low, distorted, or completely missing sound during calls and media playback.",
      problems: [
        "Low speaker volume",
        "Distorted sound",
        "No sound",
        "Crackling speaker",
        "Speaker not working",
      ],
    },

    "microphone-problems": {
      title: "Microphone Problems",
      description:
        "Microphone problems can affect phone calls, voice recordings, video calls, and voice messages.",
      problems: [
        "Other person cannot hear you",
        "Voice recordings not working",
        "Low microphone volume",
        "Distorted voice",
        "Microphone completely unavailable",
      ],
    },

    "water-damage": {
      title: "Water Damage",
      description:
        "Liquid exposure can affect multiple internal components of a mobile device and may cause problems immediately or later.",
      problems: [
        "Phone exposed to water",
        "Device not turning on",
        "Charging problems after water exposure",
        "Display problems",
        "Speaker or microphone problems",
      ],
    },

    "software-bugs": {
      title: "Software Bugs",
      description:
        "Software problems can affect the performance, stability, and normal operation of your mobile device.",
      problems: [
        "Applications crashing",
        "System errors",
        "Slow performance",
        "Software glitches",
        "Update-related problems",
      ],
    },

    "phone-overheating": {
      title: "Phone Overheating",
      description:
        "Excessive heating can affect your phone's performance and may be caused by software, battery, charging, or hardware issues.",
      problems: [
        "Phone becoming unusually hot",
        "Heating while charging",
        "Heating during normal use",
        "Performance slowing because of heat",
        "Unexpected shutdowns",
      ],
    },

    "network-problems": {
      title: "Network Problems",
      description:
        "Network problems can prevent your phone from connecting properly to mobile networks, Wi-Fi, or other communication services.",
      problems: [
        "No network signal",
        "Weak signal",
        "Mobile data not working",
        "Wi-Fi connection problems",
        "Network dropping frequently",
      ],
    },

    "data-recovery": {
      title: "Data Recovery",
      description:
        "If important photos, videos, files, or other data become inaccessible, the device can be assessed to determine whether recovery is possible.",
      problems: [
        "Deleted photos",
        "Lost videos",
        "Inaccessible files",
        "Damaged device data",
        "Device not turning on",
      ],
    },

    "boot-loop-issues": {
      title: "Boot Loop Issues",
      description:
        "A phone stuck repeatedly restarting or remaining on its startup logo may have a software or hardware-related problem.",
      problems: [
        "Phone stuck on logo",
        "Repeated restarting",
        "Device unable to boot",
        "System startup failure",
        "Unexpected restart loops",
      ],
    },

    "motherboard-faults": {
      title: "Motherboard Faults",
      description:
        "Motherboard faults can affect several functions of a mobile device and often require detailed hardware diagnosis.",
      problems: [
        "Phone not turning on",
        "Charging circuit problems",
        "Unexpected shutdowns",
        "Hardware-related failures",
        "Other complex motherboard faults",
      ],
    },
  };

  const issue = issues[slug];

  if (!issue) {
    return (
      <section className="issue-not-found">
        <div className="container text-center">
          <FaTools className="issue-not-found-icon" />

          <h2>Issue Not Found</h2>

          <p>The issue you are looking for could not be found.</p>

          <Link to="/" className="btn">
            Back To Home
          </Link>
        </div>
      </section>
    );
  }

  return (
    <main className="issue-details-page">

      {/* Hero */}
      <section className="issue-details-hero">
        <div className="container">
          <div
            className="issue-details-hero-content"
            data-aos="fade-up"
          >
            <div className="issue-details-icon">
              <FaCheckCircle />
            </div>

            <span className="section-badge">
              Common Mobile Issue
            </span>

            <h1>{issue.title}</h1>

            <p>{issue.description}</p>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="issue-details-section">
        <div className="container">
          <div className="row align-items-center">

            <div
              className="col-lg-6 mb-4 mb-lg-0"
              data-aos="fade-right"
            >
              <span className="section-badge">
                Problem Details
              </span>

              <h2 className="section-title text-start">
                Having {issue.title}?
              </h2>

              <p className="issue-details-text">
                Our technicians can inspect your device to identify
                the cause of the problem and determine the appropriate
                repair solution.
              </p>

              <p className="issue-details-text">
                We carefully diagnose the device before proceeding
                with the required repair.
              </p>
            </div>

            <div
              className="col-lg-6"
              data-aos="fade-left"
            >
              <div className="issue-problems-box">
                <h3>Common Symptoms</h3>

                <div className="issue-problems-list">
                  {issue.problems.map((problem, index) => (
                    <div
                      className="issue-problem"
                      key={index}
                    >
                      <FaCheckCircle />
                      <span>{problem}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="issue-details-cta">
        <div className="container">
          <div
            className="issue-cta-card"
            data-aos="zoom-in"
          >
            <h2>Need Help With {issue.title}?</h2>

            <p>
              Contact MR. MOB FIXER for professional mobile repair
              assistance.
            </p>

            <div className="issue-cta-buttons">

              <a
                href={`https://wa.me/${siteData.whatsapp.replace(
                  "+",
                  ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn issue-whatsapp-btn"
              >
                <FaWhatsapp />
                WhatsApp Us
              </a>

              <a
                href={`tel:${siteData.phone}`}
                className="btn issue-call-btn"
              >
                <FaPhoneAlt />
                Call Now
              </a>

            </div>
          </div>
        </div>
      </section>

      {/* Back */}
      <div className="issue-back">
        <Link to="/">
          <FaArrowLeft />
          Back To Home
        </Link>
      </div>

    </main>
  );
}

export default IssueDetails;